import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { glossaryService, CreateTermData } from '../../services/glossaryService';
import { CorpusSector } from '../../types';
import { Button } from '../ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUpload, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

interface ImportTermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sectors: CorpusSector[];
  defaultSectorId?: number;
}

export default function ImportTermsModal({
  isOpen,
  onClose,
  sectors,
  defaultSectorId,
}: ImportTermsModalProps) {
  const queryClient = useQueryClient();
  const [selectedSectorId, setSelectedSectorId] = useState(defaultSectorId || sectors[0]?.id || 0);
  const [fileContent, setFileContent] = useState<string>('');
  const [format, setFormat] = useState<'json' | 'txt'>('json');

  const importMutation = useMutation({
    mutationFn: (data: { sectorId: number; terms: CreateTermData[]; format: 'json' | 'txt' }) =>
      glossaryService.importTerms(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['glossary-terms'] });
      queryClient.invalidateQueries({ queryKey: ['glossary-stats'] });

      const { results } = data;
      if (results.errors.length > 0) {
        toast.success(
          `Importación completada: ${results.created} creados, ${results.skipped} omitidos`,
          { duration: 5000 }
        );
        console.log('Errores de importación:', results.errors);
      } else {
        toast.success(`${results.created} términos importados exitosamente`);
      }

      resetForm();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al importar términos');
    },
  });

  const resetForm = () => {
    setFileContent('');
    setFormat('json');
    setSelectedSectorId(defaultSectorId || sectors[0]?.id || 0);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setFileContent(content);

      // Auto-detect format
      if (file.name.endsWith('.json')) {
        setFormat('json');
      } else if (file.name.endsWith('.txt')) {
        setFormat('txt');
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!fileContent.trim()) {
      toast.error('Por favor, carga un archivo o pega el contenido');
      return;
    }

    try {
      let terms: CreateTermData[] = [];

      if (format === 'json') {
        // Parse JSON format
        const jsonData = JSON.parse(fileContent);

        // Support both direct array and object with terms property
        const termsArray = Array.isArray(jsonData) ? jsonData : jsonData.terms;

        if (!Array.isArray(termsArray)) {
          throw new Error('El formato JSON debe ser un array de términos o un objeto con propiedad "terms"');
        }

        terms = termsArray.map((item: any) => ({
          term: item.term,
          acronym: item.acronym || undefined,
          definition: item.definition,
          simplifiedDefinition: item.simplifiedDefinition || undefined,
          priority: item.priority || 'MEDIA',
          sectorId: selectedSectorId,
          mcerlLevel: item.mcerlLevel || 'C1',
          examples: item.examples || undefined,
          synonyms: item.synonyms || undefined,
        }));
      } else {
        // Parse TXT format (pipe-separated)
        const lines = fileContent.split('\n').filter((line) => line.trim() !== '');

        // Skip header line if present
        const startIndex = lines[0].toLowerCase().includes('term|') ? 1 : 0;

        terms = lines.slice(startIndex).map((line) => {
          const parts = line.split('|').map((p) => p.trim());

          return {
            term: parts[0],
            acronym: parts[1] || undefined,
            definition: parts[2],
            simplifiedDefinition: parts[3] || undefined,
            priority: (parts[4] as 'ALTA' | 'MEDIA' | 'BAJA') || 'MEDIA',
            sectorId: selectedSectorId,
            mcerlLevel: (parts[5] as 'B2' | 'C1' | 'C2') || 'C1',
            examples: parts[7] ? JSON.parse(parts[7]) : undefined,
            synonyms: parts[8] ? JSON.parse(parts[8]) : undefined,
          };
        });
      }

      if (terms.length === 0) {
        toast.error('No se encontraron términos válidos en el archivo');
        return;
      }

      importMutation.mutate({
        sectorId: selectedSectorId,
        terms,
        format,
      });
    } catch (error: any) {
      toast.error(`Error al procesar el archivo: ${error.message}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-purple-600 text-white p-6 rounded-t-xl flex items-center justify-between">
          <h2 className="text-2xl font-bold">Importar Términos</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 text-xl flex-shrink-0 mt-1" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-2">Formatos soportados:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>
                    <strong>JSON:</strong> Array de objetos con propiedades: term, definition, priority,
                    mcerlLevel (obligatorios) y acronym, simplifiedDefinition, examples, synonyms
                    (opcionales)
                  </li>
                  <li>
                    <strong>TXT:</strong> Una línea por término con formato pipe-separated:
                    term|acronym|definition|simplifiedDefinition|priority|mcerlLevel|sector|examples|synonyms
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sector Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sector de Destino <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedSectorId}
              onChange={(e) => setSelectedSectorId(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            >
              {sectors.map((sector) => (
                <option key={sector.id} value={sector.id}>
                  {sector.icon} {sector.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Todos los términos se asignarán a este sector
            </p>
          </div>

          {/* Format Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Formato <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="json"
                  checked={format === 'json'}
                  onChange={(e) => setFormat(e.target.value as 'json' | 'txt')}
                  className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                />
                <span>JSON</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="txt"
                  checked={format === 'txt'}
                  onChange={(e) => setFormat(e.target.value as 'json' | 'txt')}
                  className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                />
                <span>TXT (Pipe-separated)</span>
              </label>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Cargar Archivo
            </label>
            <div className="flex items-center gap-3">
              <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 cursor-pointer transition-colors">
                <FontAwesomeIcon icon={faUpload} className="text-gray-400" />
                <span className="text-sm text-gray-600">
                  Seleccionar archivo {format.toUpperCase()}
                </span>
                <input
                  type="file"
                  accept={format === 'json' ? '.json' : '.txt'}
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Text Area */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              O Pegar Contenido
            </label>
            <textarea
              value={fileContent}
              onChange={(e) => setFileContent(e.target.value)}
              rows={12}
              placeholder={
                format === 'json'
                  ? '[\n  {\n    "term": "ejemplo",\n    "definition": "definición del término",\n    "priority": "ALTA",\n    "mcerlLevel": "C1"\n  }\n]'
                  : 'term|acronym|definition|simplifiedDefinition|priority|mcerlLevel|sector|examples|synonyms'
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="px-6"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="px-6 bg-purple-600 hover:bg-purple-700"
              disabled={importMutation.isPending}
            >
              {importMutation.isPending ? 'Importando...' : 'Importar Términos'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
